import React from "react";
import { Box, Typography, Tooltip, Chip, alpha } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import { MathJaxContext, MathJax } from "better-react-mathjax";

const FlashcardResultsTooltip = ({ collection, theme, compact = false }) => {
  const getFlashcardResults = (flashcards) => {
    const results = { easy: 0, good: 0, hard: 0, fail: 0 };
    flashcards.forEach((card) => {
      if (card.correct === "easy") results.easy++;
      else if (card.correct === "good") results.good++;
      else if (card.correct === "hard") results.hard++;
      else if (card.correct === "fail") results.fail++;
    });
    return results;
  };

  const resultChips = [
    {
      name: "Väldigt svåra",
      color: "#FF4D4D",
      key: "fail",
      icon: CancelOutlinedIcon,
    },
    { name: "Svåra", color: "#C47510", key: "hard", icon: CircleIcon },
    {
      name: "Ganska lätta",
      color: "#27792B",
      key: "good",
      icon: CheckCircleOutlineIcon,
    },
    {
      name: "Lätta",
      color: "#2196F3",
      key: "easy",
      icon: CheckCircleOutlineIcon,
    },
  ];

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
              ["(", ")"],
              ["[", "]"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3,
            fontFamily: "Arial",
            matchFontHeight: false,
            displayAlign: "left",
            displayIndent: "0",
          },
        }}
      >
        <Tooltip
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
                "& .MuiTooltip-arrow": {
                  color: "background.paper",
                },
              },
            },
          }}
          title={
            <Box
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                p: 2,
                maxWidth: 400,
                maxHeight: 400,
                overflow: "auto",
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            >
              {collection.flashcards.map((card, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2.5,
                    pb: 2.5,
                    "&:not(:last-child)": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      icon={
                        card.correct === "easy" ? (
                          <CheckCircleOutlineIcon />
                        ) : card.correct === "good" ? (
                          <CheckCircleOutlineIcon />
                        ) : card.correct === "hard" ? (
                          <CircleIcon />
                        ) : (
                          <CancelOutlinedIcon />
                        )
                      }
                      label={
                        card.correct === "easy"
                          ? "Lätt"
                          : card.correct === "good"
                            ? "Ganska lätt"
                            : card.correct === "hard"
                              ? "Svår"
                              : "Väldigt svår"
                      }
                      sx={{
                        height: "24px",
                        background: alpha(
                          card.correct === "easy"
                            ? "#2196F3"
                            : card.correct === "good"
                              ? "#27792B"
                              : card.correct === "hard"
                                ? "#C47510"
                                : "#FF4D4D",
                          0.1,
                        ),
                        color:
                          card.correct === "easy"
                            ? "#2196F3"
                            : card.correct === "good"
                              ? "#27792B"
                              : card.correct === "hard"
                                ? "#C47510"
                                : "#FF4D4D",
                        fontSize: "11px",
                        fontWeight: 500,
                        "& .MuiChip-icon": {
                          fontSize: 14,
                          color: "inherit",
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Framsida:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    <MathJax>{card.front}</MathJax>
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Baksida:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    <MathJax>{card.back}</MathJax>
                  </Typography>
                </Box>
              ))}
            </Box>
          }
          arrow
          placement="top-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "background.paper",
                "& .MuiTooltip-arrow": {
                  color: "background.paper",
                },
              },
            },
          }}
        >
          <Box
            sx={{ display: "flex", gap: 1, flexWrap: "wrap", maxWidth: 400 }}
          >
            {compact ? (
              // Compact view - just one chip
              <Chip
                label="Resultat"
                size="small"
                icon={<CheckCircleOutlineIcon style={{ color: "inherit" }} />}
                sx={{
                  height: "24px",
                  background: alpha("#4CAF50", 0.1),
                  color: "#4CAF50",
                  textTransform: "uppercase",
                  fontSize: "11px",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    fontSize: 14,
                    color: "#4CAF50",
                  },
                }}
              />
            ) : (
              // Full view - all result chips
              (() => {
                const results = getFlashcardResults(collection.flashcards);
                return resultChips.map(
                  (chip) =>
                    results[chip.key] > 0 && (
                      <Chip
                        key={chip.name}
                        label={`${chip.name}: ${results[chip.key]}`}
                        size="small"
                        icon={<chip.icon style={{ color: "inherit" }} />}
                        sx={{
                          height: "24px",
                          background: alpha(chip.color, 0.1),
                          color: chip.color,
                          textTransform: "uppercase",
                          fontSize: "11px",
                          fontWeight: 500,
                          "& .MuiChip-icon": {
                            fontSize: 14,
                            color: "inherit",
                          },
                        }}
                      />
                    ),
                );
              })()
            )}
          </Box>
        </Tooltip>
      </MathJaxContext>
    </>
  );
};

export default FlashcardResultsTooltip;
